import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useConceptualCategory } from '../hooks/useConceptualCategory';
import { useSessionGuard } from '../contexts/SessionGuardContext';
import { useOrientationScrollReset } from '../hooks/useOrientationScrollReset';
import { SessionViewport } from '../components/table-session/SessionViewport';
import { TableFrame } from '../components/table-session/TableFrame';
import {
  MASTHEAD_EXIT_TAB,
  MASTHEAD_ICON_BUTTON,
  MASTHEAD_FULL_INLINE,
  MASTHEAD_FULL_BLOCK,
  MASTHEAD_PHONE_BLOCK,
} from '../components/table-session/mastheadChrome';
import { ConceptualAuctionTable } from '../components/conceptual-problems/ConceptualAuctionTable';
import { ConceptualHandPanel } from '../components/conceptual-problems/ConceptualHandsPanel';
import { ConceptualQuestionPanel } from '../components/conceptual-problems/ConceptualQuestionPanel';
import { ConceptualAnswerPanel } from '../components/conceptual-problems/ConceptualAnswerPanel';
import { ConceptualStatusLine } from '../components/conceptual-problems/ConceptualStatusLine';
import { ConceptualMastheadInfoRow } from '../components/conceptual-problems/ConceptualMastheadInfoRow';
import { ConceptualDetailsPopup } from '../components/conceptual-problems/ConceptualDetailsPopup';
import { ConceptualCategoriesControl } from '../components/conceptual-problems/ConceptualCategoriesControl';
import { ConceptualCategoryPicker } from '../components/conceptual-problems/ConceptualCategoryPicker';
import { ConceptualEditorPill } from '../components/conceptual-problems/ConceptualEditorPill';
import { ViewportSizeTag } from '../components/conceptual-problems/ViewportSizeTag';
import { conceptualProblemsApi } from '../api/conceptualProblemsApi';
import { useAuth } from '../hooks/useAuth';
import { UNCATEGORISED_SEGMENT } from '../types/conceptualProblems';
import type { ConceptualCategory, ProblemLockedError } from '../types/conceptualProblems';
// The data-editing pages' own table rules (spec 031): their viewport floor and the answer's room.
// Scoped to `.data-editing-page` below, so no learner page is touched.
import '../styles/data-editing.css';

/**
 * The read-only conceptual-problem browsing page (feature 029).
 *
 * <p>It is drawn by the SAME `TableFrame` the Bidding Practice page uses — the felt frame, the
 * cream panels, the seat-placed hand beside the vulnerability board, the shared auction panel, the
 * status line beneath — because the client asked for exactly that (2026-09-09), and FR-008 asks for
 * it too. The route is registered in `TABLE_SESSION_ROUTES`, so it inherits the whole app-like
 * phone treatment: the viewport lock, the compact masthead row with its ⓘ popup below `md`, the
 * three-panel landscape row, and the no-scrollbar contract.</p>
 *
 * <p>What it does with the frame's slots is where it differs, and every difference follows from one
 * fact: nothing here is being answered. The right-hand panel that holds a bid pad holds the
 * QUESTION instead — which on this page is a sentence rather than the fixed "What is your call?" —
 * and the full-width row that holds Jeff's commentary holds the descriptive ANSWER, shown from
 * first paint with no reveal (FR-010). There is no timer, score, streak, difficulty control or
 * verdict, and the status line carries the facts an editor checks in their place.</p>
 *
 * <p><b>Position lives in the URL, not in state.</b> `/conceptual-problems/:categoryId/:serial`
 * identifies the problem by its serial number, so a reload, a shared link and the browser's Back
 * button all behave, and a link cannot silently drift onto a different problem when the derived
 * category is recomputed on re-import. Previous/Next navigate rather than setting an index.</p>
 */
export function ConceptualProblemsPage() {
  const navigate = useNavigate();
  const { categoryId: categoryParam, serial: serialParam } = useParams<{
    categoryId: string;
    serial?: string;
  }>();

  // The viewport-locked container. iOS Safari hides the controls behind the sticky header after an
  // orientation change unless the scroll position is reset — shared with the other table sessions.
  const pageScrollRef = useRef<HTMLDivElement>(null);
  useOrientationScrollReset(pageScrollRef);

  // The phone's expansion of the masthead row. Held here because stepping to another problem must
  // close it: its facts belong to the problem that was on screen when it was opened.
  const [showDetails, setShowDetails] = useState(false);

  // The category picker (2026-09-10 amendment). Its ticks live inside the picker, not here, so
  // closing it by any route discards them without this page needing to know they existed (FR-029).
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState('');

  // Every category, for the picker's list. Loaded once with the page rather than when the picker
  // opens, so opening it is instant and never shows a spinner over the problem being read.
  const [allCategories, setAllCategories] = useState<ConceptualCategory[]>([]);
  useEffect(() => {
    let cancelled = false;
    conceptualProblemsApi
      .getCategories()
      .then((response) => { if (!cancelled) setAllCategories(response.categories); })
      // Deliberately silent: the picker is one control on a page whose job is reading. Failing to
      // load its list must not put an error banner over a problem the editor is part-way through.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // `null` is the uncategorised bucket and is a legitimate thing to ask for; `undefined` means the
  // route parameter is not a category at all, which the hook treats as "nothing to load yet" and
  // the not-found branch below reports.
  const categoryId = useMemo(() => {
    if (categoryParam === UNCATEGORISED_SEGMENT) return null;
    const parsed = Number(categoryParam);
    return Number.isInteger(parsed) ? parsed : undefined;
  }, [categoryParam]);

  const { data, loading, error, notFound, reload, reconcileSave, markLocked } =
    useConceptualCategory(categoryId);

  // A Super Editor may still change a locked problem; everyone else sees it read-only (FR-048). The
  // server decides for itself — this only chooses which picker to draw, the AskJeffButton pattern.
  const { user } = useAuth();
  const isSuperEditor = user?.role === 'Super Editor';

  // Memoised so the redirect effect below does not see a new array identity on every render.
  const items = useMemo(() => data?.items ?? [], [data]);
  const currentIndex = serialParam ? items.findIndex(p => String(p.serial) === serialParam) : 0;
  const current = currentIndex >= 0 ? items[currentIndex] : undefined;

  const categoryName = data?.categoryName ?? 'Conceptual Problems';

  // The site header's phone banner names the category as well as the surface (client request,
  // 2026-09-09). It reads the name from here rather than keeping a copy, and it cannot derive one
  // the way it derives module practice's course trail from the address: this route carries the
  // category's ID, and one live category name is 'System sequences after 1♣ or 1♦', which no slug
  // survives. Null until the category lands, and in every state that has no category to name, so
  // the header falls back to the surface name on its own.
  //
  // `hasActiveSession` is deliberately NOT set. It is what turns on the navigation guard, the
  // logout confirmation and the leave-the-page prompt, and nothing here is a session: there is no
  // attempt in progress and nothing to lose by leaving.
  const { setSessionTitle } = useSessionGuard();
  useEffect(() => {
    setSessionTitle(data?.categoryName ?? null);
    return () => setSessionTitle(null);
  }, [data?.categoryName, setSessionTitle]);

  usePageTitle(
    data
      ? `${data.categoryName} - Conceptual Problems — Hawkes Bridge`
      : 'Conceptual Problems — Hawkes Bridge',
  );

  // A category address with no serial opens on the first problem, by replacing rather than pushing
  // so Back leaves the page instead of bouncing between the two forms of the same address.
  useEffect(() => {
    if (!loading && !serialParam && items.length > 0 && categoryParam) {
      navigate(`/conceptual-problems/${categoryParam}/${items[0]!.serial}`, { replace: true });
    }
  }, [loading, serialParam, items, categoryParam, navigate]);

  const goTo = useCallback(
    (index: number) => {
      const target = items[index];
      if (target && categoryParam) {
        setShowDetails(false);
        // Stepping to another problem closes the picker and discards its unsaved ticks (FR-029):
        // ticks belong to the problem they were made on, and carrying them to the next one would
        // be a way to re-file the wrong problem without noticing.
        setShowPicker(false);
        setSaveError(null);
        setSaveNotice('');
        navigate(`/conceptual-problems/${categoryParam}/${target.serial}`);
      }
    },
    [items, categoryParam, navigate],
  );

  const handleSave = useCallback(
    async (categoryIds: number[], newCategoryName: string | null) => {
      if (!current) return;

      setSaving(true);
      setSaveError(null);

      try {
        const saved = await conceptualProblemsApi.saveProblemCategories(
          current.serial,
          categoryIds,
          newCategoryName,
        );

        reconcileSave(saved);
        setShowPicker(false);

        if (saved.createdCategory) {
          // Insert it so the picker lists it next time without a refetch, and so its count is
          // right — a category created by this save holds exactly this problem.
          setAllCategories((previous) =>
            previous.some((c) => c.id === saved.createdCategory!.id)
              ? previous
              : [...previous, { ...saved.createdCategory!, problemCount: 1 }],
          );
        }

        // Correct the counts the picker will show next time it opens.
        setAllCategories((previous) =>
          previous.map((c) => {
            const updated = saved.affectedCounts.find((a) => a.id === c.id);
            return updated ? { ...c, problemCount: updated.problemCount } : c;
          }),
        );

        const leftThisCategory =
          categoryId !== undefined &&
          (categoryId === null
            ? saved.categories.length > 0
            : !saved.categories.some((c) => c.id === categoryId));

        setSaveNotice(
          (saved.categories.length === 0
            ? 'Saved. This problem is now uncategorised.'
            : `Saved. Filed under ${saved.categories.map((c) => c.name).join(', ')}.`) +
            (leftThisCategory ? ' It is no longer in the category you are browsing.' : ''),
        );
      } catch (err: unknown) {
        // The picker stays open with the ticks intact, so a failed save is a retry rather than a
        // re-entry. The server's own message is preferred where it has one — "a category called X
        // already exists" is worth reading, and a generic sentence would throw it away.
        const body = (err as { response?: { data?: Partial<ProblemLockedError> } })?.response?.data;

        // A Super Editor locked the problem after this page loaded it (FR-048). Mark it locked in
        // place: the picker re-renders read-only with the reason on it, and the masthead pill turns
        // to a padlock, so the editor is not left retrying a save that can only be refused.
        if (body?.code === 'PROBLEM_LOCKED') {
          markLocked(current.serial, body.lastEdit ?? null);
        }

        setSaveError(body?.message ?? 'Unable to save these categories. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [current, categoryId, reconcileSave, markLocked],
  );

  const atFirst = currentIndex <= 0;
  const atLast = currentIndex >= items.length - 1;

  // FR-032. The problem is still in `items` — deliberately, so it does not vanish while the editor
  // is reading it, and so Previous and Next still reach the problems that neighboured it — but the
  // category being browsed no longer contains it, and the page has to say so rather than leave the
  // screen quietly disagreeing with the database. Only ever true after a save in this session: a
  // LATER load of the same address takes the FR-014 "not in this category" branch above instead.
  //
  // The Uncategorised bucket counts as a category being browsed, which is why this is not a plain
  // `categoryId != null` test: an editor reading that bucket and filing a problem under a real
  // topic has moved it out of what is on screen just as surely, and is owed the same note.
  // `undefined` is different again — the address is not a category at all, and the not-found
  // branch above has already dealt with it.
  const movedOut =
    current != null &&
    categoryId !== undefined &&
    (categoryId === null
      ? current.categories.length > 0
      : !current.categories.some((c) => c.id === categoryId));

  // ---------------------------------------------------------------------------------------
  // The states with no problem to draw. Each is wrapped in the SAME viewport-locked frame as the
  // table, so the page never changes height between them, and each offers the way back — once the
  // masthead is gone there is no other navigation on this surface.
  // ---------------------------------------------------------------------------------------
  if (loading) {
    return (
      <SessionViewport>
        <div className="flex-1 bg-felt-bg px-4 py-16 text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-brass"></div>
          <p className="mt-4 text-cream">Loading problems...</p>
        </div>
      </SessionViewport>
    );
  }

  if (error) {
    // A failure, not a dead end — so it offers a retry as well as the way out.
    return (
      <FeltNotice title="Unable to load these problems" message={error} role="alert">
        <button
          type="button"
          onClick={reload}
          className="inline-block rounded-md bg-brass px-6 py-3 font-medium text-on-brass hover:bg-brass-hover"
        >
          Try again
        </button>
      </FeltNotice>
    );
  }

  if (notFound) {
    return (
      <FeltNotice
        title="That category no longer exists"
        message="It may have been renamed or removed since this link was made."
        testId="conceptual-not-found"
      />
    );
  }

  if (items.length === 0) {
    // A real category that simply holds nothing yet. Not an error either.
    return (
      <FeltNotice
        title={categoryName}
        message="There are no problems in this category yet."
        testId="conceptual-empty"
      />
    );
  }

  if (!current) {
    // The serial in the address is not in this category — a stale or hand-edited link.
    return (
      <FeltNotice
        title="Problem not found"
        message="That problem is not in this category."
        testId="conceptual-not-found"
      />
    );
  }

  return (
    // `data-editing-page`: the data-editing table rules (styles/data-editing.css) — the page
    // scrolls below the editors' viewport floor rather than clipping, and the hands run the full
    // height beside the answer.
    <SessionViewport ref={pageScrollRef} className="data-editing-page">
      <ViewportSizeTag />
      <TableFrame
        // `flex-1` stretches the felt to the bottom of the viewport-locked container, so the page's
        // paper background never shows through as a pale band beneath the table.
        // `conceptual-table` is index.css's hook for making the frame fill its column (Firefox).
        className="conceptual-table flex-1 bg-felt-bg py-1.5 sm:py-6 padswap:py-0.5"
        subtitle={
          <>
            {/* md+ (and not a landscape phone): the category being read. */}
            <span className={MASTHEAD_FULL_INLINE}>{categoryName}</span>
            {/* Phone: the visible title gives way to the compact row, but the h1 keeps the name for
                screen readers so the frame's heading is never empty. */}
            <span className={`sr-only ${MASTHEAD_PHONE_BLOCK}`}>{categoryName}</span>
            <ConceptualMastheadInfoRow
              position={currentIndex + 1}
              // The category's own total, which a save can move — not the length of the array,
              // which still holds a problem the category no longer contains (FR-032).
              total={data?.total ?? items.length}
              serial={current.serial}
            />
          </>
        }
        mastheadSubtitleTrailing={
          <>
            <ConceptualCategoriesControl
              isOpen={showPicker}
              onOpen={() => setShowPicker(true)}
              locked={current.locked}
            />
            {/* Phone only: who last edited, always visible so a Super Editor stepping through the
                category never has to open a popup to see it (FR-047). */}
            <ConceptualEditorPill lastEdit={current.lastEdit} locked={current.locked} />
          </>
        }
        mastheadSubtitleAction={
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            aria-label="Problem details"
            title="Problem details"
            aria-haspopup="dialog"
            aria-expanded={showDetails}
            className={`${MASTHEAD_ICON_BUTTON} text-cream ${MASTHEAD_PHONE_BLOCK}`}
            data-testid="masthead-details-button"
          >
            <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        }
        mastheadLinks={
          // The way out, on the shared brass exit tab — the same control and the same height as
          // "End Session" on the practice pages, collapsing to its glyph on a phone.
          <Link
            to="/conceptual-problems"
            aria-label="Back to categories"
            title="Back to categories"
            className={
              MASTHEAD_EXIT_TAB +
              ' font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cream'
            }
            data-testid="conceptual-back"
          >
            <span className={MASTHEAD_FULL_INLINE}>Back to categories</span>
            <ChevronLeftIcon className={`h-5 w-5 ${MASTHEAD_PHONE_BLOCK}`} aria-hidden="true" />
          </Link>
        }
        // The seat label rides directly above the hand inside SeatBoardLayout, so it stays over the
        // cards wherever the seat places them. Suppress the panel-top heading to avoid showing it
        // twice.
        handLabel={null}
        // BOTH recorded hands live in this one panel, side by side. The frame's `secondaryHand`
        // slot would be the obvious home for the second, and it is what the 025 review surface
        // does — but that surface is not viewport-locked and this one is, and a fourth column
        // does not survive the locked bands. See ConceptualHandPanel for the measurements.
        hand={<ConceptualHandPanel problem={current} />}
        auction={
          <ConceptualAuctionTable
            auction={current.auction}
            vulnerability={current.vulnerability}
            decisionSeat={(current.decisionSeat || 'N') as 'N' | 'E' | 'S' | 'W'}
            serial={current.serial}
          />
        }
        callLabel="Question"
        call={
          <ConceptualQuestionPanel
            question={current.question}
            onPrevious={() => goTo(currentIndex - 1)}
            onNext={() => goTo(currentIndex + 1)}
            atFirst={atFirst}
            atLast={atLast}
          />
        }
        explain={<ConceptualAnswerPanel answer={current.answer} />}
        statusLine={
          // Desktop only: on phones the masthead's compact row carries the essentials and the ⓘ
          // popup carries the words, so drawing the full line here would repeat them in a space
          // that has none to spare.
          <div className={MASTHEAD_FULL_BLOCK}>
            <ConceptualStatusLine
              problem={current}
              position={currentIndex + 1}
              total={data?.total ?? items.length}
              movedOut={movedOut}
            />
          </div>
        }
      />

      {showDetails && (
        <ConceptualDetailsPopup
          problem={current}
          position={currentIndex + 1}
          total={data?.total ?? items.length}
          movedOut={movedOut}
          onClose={() => setShowDetails(false)}
        />
      )}

      {showPicker && (
        <ConceptualCategoryPicker
          categories={allCategories}
          selected={current.categories}
          isSaving={saving}
          error={saveError}
          onSave={handleSave}
          onCancel={() => {
            setShowPicker(false);
            setSaveError(null);
          }}
          locked={current.locked}
          lastEdit={current.lastEdit}
          canEditLocked={isSuperEditor}
        />
      )}

      {/* The save confirmation (FR-030). A polite live region on the page, NOT the shared `Toast`:
          the toast container is `position: fixed` at the bottom-right, which sits outside the
          locked-viewport contract this route lives under, and the data-editing and table-session
          pages already announce this way. Visually hidden — the picker closing and the categories
          changing in the status line are the sighted confirmation. */}
      <div className="sr-only" role="status" aria-live="polite" data-testid="conceptual-save-notice">
        {saveNotice}
      </div>
    </SessionViewport>
  );
}

/**
 * A state with no problem to show, on the felt and inside the height lock — the same shape the
 * practice pages use for "no problems available", so the page's geometry never changes between a
 * loaded table and a message about why there is not one.
 *
 * Every one of them offers the way back to the categories: once the frame is gone so is its
 * masthead, and this page has no other navigation of its own.
 */
function FeltNotice({
  title,
  message,
  testId,
  role,
  children,
}: {
  title: string;
  message: string;
  testId?: string;
  role?: 'alert';
  children?: ReactNode;
}) {
  return (
    <SessionViewport>
      <div className="flex-1 bg-felt-bg px-4 py-12 text-center" data-testid={testId} role={role}>
        <h2 className="mb-4 text-2xl font-bold text-cream">{title}</h2>
        <p className="mx-auto mb-6 max-w-xl text-sand">{message}</p>
        {children}
        <div className="mt-4">
          <Link
            to="/conceptual-problems"
            className="inline-block rounded-md bg-brass px-6 py-3 font-medium text-on-brass hover:bg-brass-hover"
          >
            Back to categories
          </Link>
        </div>
      </div>
    </SessionViewport>
  );
}
